import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, User, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/bookings`)
      ]);
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d. MMM yyyy, HH:mm", { locale: da });
    } catch {
      return dateString;
    }
  };

  const projectTypeLabels = {
    komplet: "Komplet Renovering",
    kokken: "Køkken",
    badevaerelse: "Badeværelse",
    maling: "Maling & Finish",
    gulve: "Gulve",
    andet: "Andet"
  };

  const budgetLabels = {
    "under-50k": "Under 50.000 kr",
    "50k-100k": "50.000 - 100.000 kr",
    "100k-250k": "100.000 - 250.000 kr",
    "250k-500k": "250.000 - 500.000 kr",
    "over-500k": "Over 500.000 kr"
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a 
                href="/" 
                className="p-2 hover:bg-white/10 rounded transition-colors"
                data-testid="admin-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </a>
              <div>
                <h1 
                  className="text-2xl font-bold"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Admin Panel
                </h1>
                <p className="text-sm text-primary-foreground/70">
                  Salgsrenovering.dk
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={fetchData}
              disabled={isLoading}
              className="rounded-none"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Opdater
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 border border-border"
            data-testid="stat-total-inquiries"
          >
            <div className="text-sm text-muted-foreground uppercase tracking-wide">
              Totale Henvendelser
            </div>
            <div className="text-4xl font-bold text-primary mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {contacts.length + bookings.length}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 border border-border"
            data-testid="stat-bookings"
          >
            <div className="text-sm text-muted-foreground uppercase tracking-wide">
              Booking Anmodninger
            </div>
            <div className="text-4xl font-bold text-accent mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {bookings.length}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 border border-border"
            data-testid="stat-contacts"
          >
            <div className="text-sm text-muted-foreground uppercase tracking-wide">
              Kontakt Beskeder
            </div>
            <div className="text-4xl font-bold text-foreground mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {contacts.length}
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary rounded-none p-1">
            <TabsTrigger 
              value="bookings" 
              className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-bookings"
            >
              Bookinger ({bookings.length})
            </TabsTrigger>
            <TabsTrigger 
              value="contacts"
              className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-contacts"
            >
              Kontakter ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Indlæser...</div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-white border border-border">
                Ingen booking anmodninger endnu
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-border p-6"
                    data-testid={`booking-card-${index}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {booking.name}
                          </h3>
                          <Badge variant="secondary" className="rounded-none">
                            {projectTypeLabels[booking.project_type] || booking.project_type}
                          </Badge>
                          {booking.budget_range && (
                            <Badge variant="outline" className="rounded-none">
                              {budgetLabels[booking.budget_range] || booking.budget_range}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${booking.email}`} className="hover:text-primary">
                              {booking.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${booking.phone}`} className="hover:text-primary">
                              {booking.phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            <span>{booking.address}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>Ønsket: {booking.preferred_date}</span>
                          </div>
                        </div>

                        {booking.additional_info && (
                          <div className="pt-2 border-t border-border mt-3">
                            <p className="text-sm text-muted-foreground">
                              <strong>Yderligere info:</strong> {booking.additional_info}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(booking.created_at)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Indlæser...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-white border border-border">
                Ingen kontakt beskeder endnu
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((contact, index) => (
                  <motion.div
                    key={contact.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-border p-6"
                    data-testid={`contact-card-${index}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {contact.name}
                          </h3>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${contact.email}`} className="hover:text-primary">
                              {contact.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${contact.phone}`} className="hover:text-primary">
                              {contact.phone}
                            </a>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-border mt-3">
                          <p className="text-sm text-foreground">
                            {contact.message}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(contact.created_at)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
